'use client';

import { useState, useCallback } from 'react';
import { EditorInstance } from 'novel';
import { getPublicAssetUrl } from '@/utils/assetUrl';

interface UseImageUploadOptions {
    editorInstance: EditorInstance | null;
}

export function useImageUpload({ editorInstance }: UseImageUploadOptions) {
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const handleAssetSelect = useCallback(async (asset: any) => {
        if (editorInstance) {
            try {
                // Determine if it's a video or image based on filename extension as fallback
                const filename = asset.meta.filename || '';
                const isVideoByExtension = /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(filename);
                const isVideo = asset.meta.contentType?.startsWith('video/') || isVideoByExtension;

                const nodeType = isVideo ? 'video' : 'image';

                const src = asset.uuid
                    ? getPublicAssetUrl(asset.uuid, isVideo ? 'video' : 'image')
                    : asset.meta.url || '';

                editorInstance.chain().focus().insertContent({
                    type: nodeType,
                    attrs: {
                        src,
                        alt: asset.meta.alt || asset.meta.filename,
                    },
                }).run();
            } catch (error) {
                console.error('Failed to insert asset:', error);
                // Fallback to markdown text if preview fails
                const fallbackSrc = asset.uuid
                    ? getPublicAssetUrl(asset.uuid, 'image')
                    : asset.meta.url || '';
                const markdown = `![${asset.meta.filename}](${fallbackSrc})`;
                editorInstance.chain().focus().insertContent(markdown).run();
            }
        } else {
            console.error('No editor instance available');
        }
    }, [editorInstance]);

    const uploadPastedImageAndInsert = useCallback(async (file: File) => {
        if (!editorInstance) return;

        try {
            setIsUploadingImage(true);
            const { assetsApi } = await import('@/app/api/assets');
            const verification = await assetsApi.requestUpload(file);

            if (verification.isDuplicate) {
                const duplicateAsset = {
                    uuid: verification.uuid,
                    meta: {
                        filename: verification.filename,
                        contentType: verification.contentType,
                        size: verification.size,
                    },
                };
                await handleAssetSelect(duplicateAsset);
                return;
            }

            if (!verification.uploadUrl || !verification.r2Key) {
                console.error('Missing upload URL or R2 key for pasted image upload');
                return;
            }

            const hexPairs = verification.md5.match(/\w{2}/g);
            if (!hexPairs) {
                console.error('Invalid MD5 hash format for pasted image upload');
                return;
            }
            const md5Base64 = btoa(hexPairs.map((byte) => String.fromCharCode(parseInt(byte, 16))).join(''));

            const uploadResponse = await fetch(verification.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                    'Content-Length': file.size.toString(),
                    'Content-MD5': md5Base64,
                },
            });

            if (!uploadResponse.ok) {
                console.error('Upload failed for pasted image:', uploadResponse.statusText);
                return;
            }

            const metadata = {
                filename: file.name || 'pasted-image',
                description: '',
                tags: [],
                assetType: 'image',
                contentType: file.type,
                size: file.size,
            };

            const asset = await assetsApi.createAsset(
                verification.uuid,
                verification.md5,
                verification.r2Key,
                metadata
            );

            await handleAssetSelect(asset);
        } catch (error) {
            console.error('Error uploading pasted image:', error);
        } finally {
            setIsUploadingImage(false);
        }
    }, [editorInstance, handleAssetSelect]);

    const handleImagePaste = useCallback((event: any) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) {
            return false;
        }

        const items = clipboardData.items ? Array.from(clipboardData.items) : [];
        let files: File[] = [];

        if (items.length > 0) {
            files = items
                .filter((item) => (item as DataTransferItem).kind === 'file' && typeof (item as DataTransferItem).type === 'string' && (item as DataTransferItem).type.startsWith('image/'))
                .map((item) => (item as DataTransferItem).getAsFile())
                .filter((file): file is File => !!file && typeof file.type === 'string' && file.type.startsWith('image/'));
        } else if (clipboardData.files && clipboardData.files.length > 0) {
            files = Array.from(clipboardData.files as FileList).filter(
                (file) => typeof file.type === 'string' && file.type.startsWith('image/')
            );
        }

        if (!files.length) {
            return false;
        }

        event.preventDefault();
        const file = files[0];
        uploadPastedImageAndInsert(file);
        return true;
    }, [uploadPastedImageAndInsert]);

    return {
        isUploadingImage,
        handleAssetSelect,
        handleImagePaste,
        uploadPastedImageAndInsert,
    };
}
